import styled from "styled-components";

import { Button, FormSize } from "ui/ecosystems/forms";

type ConnectButtonState = "connected" | "connecting" | "disabled" | "active";

interface ConnectButtonProps {
  className?: string;
  state: ConnectButtonState;
  onClick(): void;
}

function getLabel(state: ConnectButtonState) {
  switch (state) {
    case "connected":
      return "Connected";
    case "connecting":
      return "Connecting...";
    default:
      return "Connect";
  }
}

export type GetConnectButtonState<T> = (item: T) => ConnectButtonState;

function getConnectButtonState<T>(
  item: T,
  {
    isConnected,
    isConnecting,
    isDisabled,
  }: {
    isConnected(i: T): boolean;
    isConnecting(i: T): boolean;
    isDisabled(i: T): boolean;
  }
): ConnectButtonState {
  if (isConnected(item)) {
    return "connected";
  } else if (isConnecting(item)) {
    return "connecting";
  } else if (isDisabled(item)) {
    return "disabled";
  } else {
    return "active";
  }
}

export function getConnectButtonStateFactory<T>(predicates: {
  isConnected(i: T): boolean;
  isConnecting(i: T): boolean;
  isDisabled(i: T): boolean;
}): GetConnectButtonState<T> {
  return (item: T) => getConnectButtonState(item, predicates);
}

export function ConnectButton({
  className,
  state,
  onClick,
}: ConnectButtonProps) {
  if (state === "connected") {
    return (
      <ConnectedDiv className={className} as="div">
        {getLabel(state)}
      </ConnectedDiv>
    );
  }

  return (
    <StyledButton
      primary
      size={FormSize.small}
      className={className}
      disabled={["connecting", "disabled"].some((s) => s === state)}
      onClick={() => onClick()}
    >
      {getLabel(state)}
    </StyledButton>
  );
}

const StyledButton = styled(Button)`
  min-width: 100px;
  display: inline-block;
`;

const ConnectedDiv = styled.div`
  width: 100px;
  height: 30px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-size: 10px;
  line-height: 125.5%;
  font-weight: 500;
  background: #b0ffb0;
  color: green;
  border-radius: 5px;
`;
